import { useEffect, useRef, useState } from "react";
import { getAdminHeaders, uploadAdminImage, toAssetUrl } from "./adminApi";
import { canPlayInline, extractHttpUrl } from "../../utils/videoEmbed";

const emptyCourse = {
  title: "",
  description: "",
  category: "dating",
  audience: "all",
  access_level: "public",
  platform: "bilibili",
  video_url: "",
  embed_url: "",
  cover_url: "",
  duration_label: "",
  is_enabled: 1,
  is_featured: 0,
  sort_order: 0,
};

export default function AdminVideoCourses() {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState({});
  const [platforms, setPlatforms] = useState({});
  const [accessLevels, setAccessLevels] = useState({});
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(emptyCourse);
  const [message, setMessage] = useState("");
  const [coverUploading, setCoverUploading] = useState(false);
  const coverInputRef = useRef(null);

  const pageSize = 20;

  const loadItems = async () => {
    setLoading(true);
    try {
      const res = await fetch(`/api/admin/video-courses?page=${page}&page_size=${pageSize}`, {
        headers: getAdminHeaders(),
      });
      const data = await res.json();
      if (res.ok) {
        setItems(data.items || []);
        setTotal(data.total || 0);
        setCategories(data.categories || {});
        setPlatforms(data.platforms || {});
        setAccessLevels(data.access_levels || {});
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadItems();
  }, [page]);

  const openCreate = () => {
    setCreating(true);
    setEditing(null);
    setForm({ ...emptyCourse, sort_order: total + 1 });
  };

  const openEdit = (item) => {
    setEditing(item);
    setCreating(false);
    setForm({ ...item });
  };

  const handleCoverUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCoverUploading(true);
    try {
      const url = await uploadAdminImage(file);
      setForm((prev) => ({ ...prev, cover_url: url }));
    } catch (err) {
      alert(err.message || "上传失败");
    } finally {
      setCoverUploading(false);
      if (coverInputRef.current) coverInputRef.current.value = "";
    }
  };

  const save = async () => {
    if (!form.title.trim() || !form.video_url.trim()) {
      alert("请填写标题和视频链接");
      return;
    }
    const payload = {
      ...form,
      is_enabled: Number(form.is_enabled),
      is_featured: Number(form.is_featured),
      sort_order: Number(form.sort_order),
    };
    const url = editing ? `/api/admin/video-courses/${editing.id}` : "/api/admin/video-courses";
    const res = await fetch(url, {
      method: editing ? "PATCH" : "POST",
      headers: getAdminHeaders(true),
      body: JSON.stringify(payload),
    });
    const data = await res.json();
    if (!res.ok) {
      alert(data.detail || "保存失败");
      return;
    }
    setEditing(null);
    setCreating(false);
    setMessage("已保存");
    loadItems();
  };

  const remove = async (item) => {
    if (!window.confirm(`删除课程「${item.title}」？`)) return;
    await fetch(`/api/admin/video-courses/${item.id}`, {
      method: "DELETE",
      headers: getAdminHeaders(),
    });
    setMessage("已删除");
    loadItems();
  };

  const showModal = creating || editing;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">视频课程管理</h1>
          <p className="text-sm text-gray-500 mt-1">
            视频上传到 B 站、腾讯视频等第三方，此处填播放链接与嵌入地址，不占本站存储。
          </p>
        </div>
        <button onClick={openCreate} className="px-4 py-2 bg-pink-500 text-white rounded-lg text-sm">
          添加课程
        </button>
      </div>

      {message && <div className="bg-green-50 text-green-700 text-sm px-4 py-2 rounded-lg">{message}</div>}

      <div className="bg-white rounded-xl shadow overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-500">
            <tr>
              <th className="text-left p-3">标题</th>
              <th className="text-left p-3">分类</th>
              <th className="text-left p-3">观看权限</th>
              <th className="text-left p-3">平台</th>
              <th className="text-left p-3">状态</th>
              <th className="text-left p-3">操作</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} className="p-8 text-center text-gray-400">
                  加载中...
                </td>
              </tr>
            ) : (
              items.map((item) => (
                <tr key={item.id} className="border-t">
                  <td className="p-3 font-medium">{item.title}</td>
                  <td className="p-3">{item.category_label}</td>
                  <td className="p-3">{item.access_level_label || "所有人"}</td>
                  <td className="p-3">{item.platform_label}</td>
                  <td className="p-3">{item.is_enabled ? "展示" : "隐藏"}</td>
                  <td className="p-3 space-x-2">
                    <button type="button" onClick={() => openEdit(item)} className="text-blue-600">
                      编辑
                    </button>
                    <button type="button" onClick={() => remove(item)} className="text-red-500">
                      删除
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6 space-y-3">
            <h3 className="font-bold">{editing ? "编辑课程" : "添加课程"}</h3>
            <p className="text-xs text-gray-500 leading-relaxed bg-amber-50 rounded-lg p-3 space-y-2">
              <strong>一句话：</strong>视频文件在 B 站/腾讯，你们网站只填「链接」，用他们的播放器在弹窗里播，不用自己存视频。
              <br />
              <strong>哔哩哔哩：</strong>「视频原页面链接」整段粘贴分享文案（有中文没关系）或浏览器地址栏链接，含 BV 即可；「站内播放地址」<strong>留空</strong>。
              <br />
              <strong>腾讯 / 优酷：</strong>「视频原页面链接」填网页地址；「站内播放地址」填分享 → 嵌入代码里 iframe 的 src（见下方说明）。
            </p>
            {form.video_url && (
              <p
                className={`text-xs px-3 py-2 rounded-lg ${
                  canPlayInline(form) ? "bg-green-50 text-green-700" : "bg-gray-50 text-gray-600"
                }`}
              >
                {canPlayInline(form)
                  ? "✓ 按当前填写，前台可在本站弹窗内播放"
                  : "○ 当前可能无法在本站播放，访客需跳转原网站（请检查链接或补填站内播放地址）"}
              </p>
            )}
            <input
              placeholder="课程标题"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="w-full border rounded-lg px-3 py-2 text-sm"
            />
            <textarea
              placeholder="课程简介"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={3}
              className="w-full border rounded-lg px-3 py-2 text-sm"
            />
            <select
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              className="w-full border rounded-lg px-3 py-2 text-sm"
            >
              {Object.entries(categories).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </select>
            <label className="block text-sm">
              <span className="text-gray-600">适合人群（婚姻状态标签，不影响观看权限）</span>
              <select
                value={form.audience}
                onChange={(e) => setForm({ ...form, audience: e.target.value })}
                className="mt-1 w-full border rounded-lg px-3 py-2 text-sm"
              >
                <option value="all">全部人群（单身+已婚）</option>
                <option value="single">面向单身</option>
                <option value="married">面向已婚</option>
              </select>
            </label>
            <label className="block text-sm">
              <span className="text-gray-600">观看权限</span>
              <select
                value={form.access_level || "public"}
                onChange={(e) => setForm({ ...form, access_level: e.target.value })}
                className="mt-1 w-full border rounded-lg px-3 py-2 text-sm"
              >
                {Object.entries(accessLevels).length > 0
                  ? Object.entries(accessLevels).map(([k, v]) => (
                      <option key={k} value={k}>
                        {v}
                      </option>
                    ))
                  : (
                    <>
                      <option value="public">所有人</option>
                      <option value="member">登录会员</option>
                      <option value="paid">付费会员</option>
                    </>
                  )}
              </select>
            </label>
            <select
              value={form.platform}
              onChange={(e) => setForm({ ...form, platform: e.target.value })}
              className="w-full border rounded-lg px-3 py-2 text-sm"
            >
              {Object.entries(platforms).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </select>
            <label className="block text-sm">
              <span className="text-gray-600">① 视频原页面链接（必填）</span>
              <input
                placeholder="B站：可直接粘贴「分享→复制链接」整段（含中文标题）；或粘贴浏览器地址栏 https://..."
                value={form.video_url}
                onChange={(e) => setForm({ ...form, video_url: e.target.value })}
                onBlur={(e) => {
                  const cleaned = extractHttpUrl(e.target.value);
                  if (cleaned && cleaned !== e.target.value) {
                    setForm((prev) => ({ ...prev, video_url: cleaned }));
                  }
                }}
                className="mt-1 w-full border rounded-lg px-3 py-2 text-sm"
              />
              <span className="text-xs text-gray-400 mt-1 block">
                填浏览器顶部地址栏的网址，或 App「分享→复制链接」整段粘贴均可（系统会自动提取 https 链接）。
                B 站短链 b23.tv 若不能播，请用浏览器打开该短链后，再复制地址栏里带 BV 的长链接。
              </span>
            </label>
            <label className="block text-sm">
              <span className="text-gray-600">② 站内播放地址（B 站留空；腾讯/优酷必填）</span>
              <textarea
                placeholder={'腾讯示例：在视频页点「分享」→「嵌入代码」，粘贴整段：<iframe src="https://v.qq.com/txp/iframe/player.html?vid=xxx" ...></iframe>\n也可只粘贴 src 里 https 开头的那一串'}
                value={form.embed_url}
                onChange={(e) => setForm({ ...form, embed_url: e.target.value })}
                rows={3}
                className="mt-1 w-full border rounded-lg px-3 py-2 text-sm font-mono text-xs"
              />
              <span className="text-xs text-gray-400 mt-1 block">
                不是填「复制链接」！要填「嵌入代码」里 iframe 的 src 地址。B 站一般不需要填这一项。
              </span>
            </label>
            <details className="text-xs text-gray-600 bg-gray-50 rounded-lg p-3">
              <summary className="cursor-pointer font-medium text-gray-700">图文：各平台填什么？</summary>
              <div className="mt-2 space-y-3 leading-relaxed">
                <div>
                  <p className="font-medium text-pink-600">哔哩哔哩（最简单）</p>
                  <p>只填 ①，② 留空。① 可以粘贴类似：</p>
                  <p className="text-gray-500">【某某课程第一讲】 https://www.bilibili.com/video/BV1xx411c7mD</p>
                  <p>或只粘贴 https://www.bilibili.com/video/BV1xx411c7mD</p>
                  <p className="text-gray-500 mt-1">番剧/晚会等链接含 /ep 编号（如 …/bangumi/play/ep3915324）也可自动识别，② 仍留空。</p>
                </div>
                <div>
                  <p className="font-medium text-pink-600">腾讯视频</p>
                  <p>① 填浏览器地址栏，如 https://v.qq.com/x/cover/…/xxx.html</p>
                  <p>② 点视频下方「分享」→「嵌入代码」，复制里面 src=&quot;https://v.qq.com/txp/iframe/player.html?vid=……&quot; 整段 iframe 或仅 src 链接</p>
                </div>
                <div>
                  <p className="font-medium text-pink-600">优酷</p>
                  <p>① 填视频页网址；② 填分享 → 嵌入代码里的 src（类似腾讯）</p>
                </div>
              </div>
            </details>
            <div className="block text-sm space-y-2">
              <span className="text-gray-600">课程封面</span>
              {form.cover_url ? (
                <img src={toAssetUrl(form.cover_url)} alt="" className="w-full max-w-[200px] h-28 object-cover rounded-lg border" />
              ) : (
                <p className="text-xs text-gray-400">未上传封面</p>
              )}
              <input
                ref={coverInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleCoverUpload}
              />
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => coverInputRef.current?.click()}
                  disabled={coverUploading}
                  className="px-3 py-1.5 border rounded-lg text-sm disabled:opacity-50"
                >
                  {coverUploading ? "上传中..." : "上传封面图片"}
                </button>
                {form.cover_url && (
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, cover_url: "" })}
                    className="text-red-500 text-sm"
                  >
                    移除
                  </button>
                )}
              </div>
            </div>
            <input
              placeholder="时长说明，如：共 8 讲"
              value={form.duration_label}
              onChange={(e) => setForm({ ...form, duration_label: e.target.value })}
              className="w-full border rounded-lg px-3 py-2 text-sm"
            />
            <div className="flex gap-4 text-sm">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={Number(form.is_enabled) === 1}
                  onChange={(e) => setForm({ ...form, is_enabled: e.target.checked ? 1 : 0 })}
                />
                前台展示
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={Number(form.is_featured) === 1}
                  onChange={(e) => setForm({ ...form, is_featured: e.target.checked ? 1 : 0 })}
                />
                推荐
              </label>
            </div>
            <div className="flex gap-2 pt-2">
              <button type="button" onClick={save} className="px-4 py-2 bg-pink-500 text-white rounded-lg text-sm">
                保存
              </button>
              <button
                type="button"
                onClick={() => {
                  setEditing(null);
                  setCreating(false);
                }}
                className="px-4 py-2 border rounded-lg text-sm"
              >
                取消
              </button>
            </div>
          </div>
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex justify-center gap-3">
          <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="px-4 py-2 border rounded-lg text-sm">
            上一页
          </button>
          <span className="text-sm self-center">
            {page}/{totalPages}
          </span>
          <button
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
            className="px-4 py-2 border rounded-lg text-sm"
          >
            下一页
          </button>
        </div>
      )}
    </div>
  );
}
